import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1Questions, initialAdditionalQuestions, ageAndBlindnessOptions, tableData,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

const Table = styled(Flex)`
  display: flex;
  border-left: 0.5px solid black;
  border-top: 0.5px solid black;
`;

const Cell = styled(Flex)`
  border-right: 0.5px solid black;
  border-bottom: 0.5px solid black;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  width: 61px;
`;

function VirginiaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFullName,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [isExemptFromWithholding, setisExemptFromWithholding] = useState(data.is_exempt || false);
  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(data.exemption || '');
  const [exemptionsError, setExemptionsError] = useState(false);

  const [claimYourself, setClaimYourself] = useState(data.claim_yourself || false);
  const [marriedAndSpouseNotClaimed, setMarriedAndSpouseNotClaimed] = useState(data.married_and_spouse_not_claimed || false);
  const [dependents, setDependents] = useState(data.dependents || '');

  const [yourselfAge65, setYourselfAge65] = useState(data.yourself_age_65 || false);
  const [spouseAge65, setSpouseAge65] = useState(data.spouse_age_65 || false);
  const [yourselfBlind, setYourselfBlind] = useState(data.yourself_blind || false);
  const [spouseBlind, setSpouseBlind] = useState(data.spouse_blind || false);

  const additionalForm = useRef();
  const additionalQuestionsWithAnswer = getInitialQuestions(initialAdditionalQuestions);
  const [additionalQuestions, setAdditionalQuestions] = useState(additionalQuestionsWithAnswer);

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                width="auto"
                onChange={(e) => {
                  setisExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                width="auto"
                onChange={(e) => {
                  setisExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
          {/* {exemptionsError && exemptionsFromWithholding === "" && (<Flex justifyContent="flex-start" mt={1}>
              <Text color="red">This is a mandatory field.</Text>
            </Flex>)} */}
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderTable = () => (
    <Table column>
      <Flex>
        {tableData.row1.map((e) => (
          <Cell>
            {e}
          </Cell>
        ))}
      </Flex>
      <Flex>
        {tableData.row2.map((e) => (
          <Cell>
            {e}
          </Cell>
        ))}
      </Flex>
      <Flex>
        {tableData.row3.map((e) => (
          <Cell>
            {e}
          </Cell>
        ))}
      </Flex>
      <Flex>
        {tableData.row4.map((e) => (
          <Cell>
            {e}
          </Cell>
        ))}
      </Flex>
    </Table>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="10px" isRequired={isExemptFromWithholding}>
        Exemptions from withholding
      </S.QuestionLabel>
      <S.QuestionWrapper mb="24px">
        <label>
          <Flex mb="10px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_one"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_one'}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
              disabled={isReviewStep}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text mb="15px">Select this option if you are not subject to Virginia withholding because you meet any one of the conditions:</Text>
              <Text mb="15px">(a) You had no liability for Virginia income tax last year and you do not expect to have any liability for this year.</Text>
              <Text mb="15px">(b) You expect your Virginia adjusted gross income to be less than the amount shown below for your filing status:</Text>
              <Flex mb="15px">
                {renderTable()}
                {/* <img src="/j/images/employee-onboarding/virginia-exempt-from-withholding.png" /> */}
              </Flex>
              <Text mb="15px">(c) You live in Kentucky or the District of Columbia and commute on a daily basis to your place of employment in Virginia.</Text>
              <Text>(d) You are a domiciliary or legal resident of Maryland, Pennsylvania or West Virginia whose only Virginia source income is from salaries and wages and such salaries and wages are subject to income taxation by your state of domicile.</Text>
            </Flex>
          </Flex>
        </label>
        <label>
          <Flex>
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_two"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_two'}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
              disabled={isReviewStep}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text>Select this option if you are not subject to Virginia withholding because you  meet the conditions set forth Under the Service member Civil Relief Act, as amended by the Military Spouses Residency Relief Act.</Text>
            </Flex>
          </Flex>
        </label>
        {exemptionsError && exemptionsFromWithholding === '' && (
          <Flex justifyContent="flex-start" mt={1}>
            <Text color="red">This is a mandatory field.</Text>
          </Flex>
        )}
      </S.QuestionWrapper>
    </Flex>
  );

  const renderExemptionsForAgeAndBlindness = (isReviewStep) => (
    <S.QuestionWrapper mb="20px">
      <Text style={{ fontWeight: 'bold', marginBottom: '20px' }}>Exemptions for age and blindness</Text>
      <Flex>
        <Flex column>
          <S.QuestionLabel>
            Check any boxes that apply to claim an exemptions:
          </S.QuestionLabel>
          {ageAndBlindnessOptions.map((e) => {
            let value;
            let handleChange;
            switch (e.id) {
              case 'yourself_age_65':
                value = yourselfAge65;
                handleChange = setYourselfAge65;
                break;
              case 'spouse_age_65':
                value = spouseAge65;
                handleChange = setSpouseAge65;
                break;
              case 'yourself_blind':
                value = yourselfBlind;
                handleChange = setYourselfBlind;
                break;
              case 'spouse_blind':
                value = spouseBlind;
                handleChange = setSpouseBlind;
                break;
            }
            if ((e.id === 'spouse_age_65' || e.id === 'spouse_blind') && !marriedAndSpouseNotClaimed) {
              return null;
            }
            return (
              <label>
                <Flex alignItems="center">
                  <S.Input
                    type="checkbox"
                    value={value}
                    onChange={(e) => {
                      handleChange(e.target.checked);
                    }}
                    checked={value}
                    disabled={isReviewStep}
                  />
                  <Box ml={1} />
                  {e.title}
                </Flex>
              </label>
            );
          })}
        </Flex>
      </Flex>
    </S.QuestionWrapper>
  );

  const renderExemptionsQuestions = (isReviewStep) => (
    <div>
      <Text style={{ fontWeight: 'bold', marginBottom: '20px' }}>Personal exemptions</Text>
      <Flex column mb="20px">
        <S.QuestionLabel mb="7px">
          Check any boxes that apply:
        </S.QuestionLabel>
        <S.QuestionWrapper>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={claimYourself}
                checked={claimYourself}
                onChange={(e) => setClaimYourself(e.target.checked)}
                disabled={isReviewStep}
              />
              <Box ml={1} />
              Your wish to claim yourself.
            </Flex>
          </label>
          <label>
            <Flex>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={marriedAndSpouseNotClaimed}
                checked={marriedAndSpouseNotClaimed}
                onChange={(e) => setMarriedAndSpouseNotClaimed(e.target.checked)}
                disabled={isReviewStep}
              />
              <Box ml={1} />
              You are married and your spouse is not claimed on his or her own certificate.
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
      <Flex column mb="20px">
        <S.QuestionLabel>
          Enter the number of dependents you be allowed to claim on your income tax return (do not include your spouse).
        </S.QuestionLabel>
        <S.QuestionWrapper mt="7px">
          <Flex mb="10px">
            <S.Input
              type="text"
                // if any value is selected then this will become disabled and set to the calculated, else it's open ended
              value={dependents}
              onChange={(e) => setDependents(e.target.value)}
            />
          </Flex>
        </S.QuestionWrapper>
      </Flex>
      {renderExemptionsForAgeAndBlindness(isReviewStep)}
      <FormConsumer
        questions={additionalQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalQuestions(questions);
        }}
        ref={additionalForm}
      />
    </div>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderExemptionsQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!isExemptFromWithholding) {
              if (additionalForm && additionalForm.current) {
                additionalForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
            } else if (exemptionsFromWithholding === '') {
              err = true;
              setExemptionsError(true);
            } else {
              setExemptionsError(false);
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let exemptionTemp = '';
            let claimYourselfTemp = false;
            let claimMarriedTemp = false;
            let dependentsTemp = '0';
            let yourself65Temp = false;
            let spouse65Temp = false;
            let yourselfBlindTemp = false;
            let spouseBlindTemp = false;
            let additionalData;
            if (!isExemptFromWithholding) {
              claimYourselfTemp = claimYourself;
              claimMarriedTemp = marriedAndSpouseNotClaimed;
              if (dependents) dependentsTemp = dependents.toString();
              if (!marriedAndSpouseNotClaimed) {
                spouse65Temp = false;
                spouseBlindTemp = false;
              } else {
                spouse65Temp = spouseAge65;
                spouseBlindTemp = spouseBlind;
              }
              yourself65Temp = yourselfAge65;
              yourselfBlindTemp = yourselfBlind;
              additionalData = additionalQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            } else {
              exemptionTemp = exemptionsFromWithholding;
              additionalData = additionalQuestionsWithAnswer.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            }
            const calculatedLines1To3 = () => {
              let value = 0;
              if (claimYourselfTemp) value += 1;
              if (claimMarriedTemp) value += 1;
              if (dependentsTemp) value += parseInt(dependentsTemp);
              return value;
            };
            const calculatedLines5To6 = () => {
              let value = 0;
              if (yourself65Temp) value += 1;
              if (spouse65Temp) value += 1;
              if (yourselfBlindTemp) value += 1;
              if (spouseBlindTemp) value += 1;
              return value;
            };
            const calculatedLines4And7 = () => calculatedLines1To3() + calculatedLines5To6();

            const claimData = {
              claim_yourself: claimYourselfTemp, married_and_spouse_not_claimed: claimMarriedTemp, dependents: dependentsTemp, line_1_to_3: calculatedLines1To3().toString(), line_5_to_6: calculatedLines5To6().toString(), total_exemptions_line_4_and_7: calculatedLines4And7().toString(),
            };
            const ageAndBlindnessData = {
              yourself_age_65: yourself65Temp, spouse_age_65: spouse65Temp, yourself_blind: yourselfBlindTemp, spouse_blind: spouseBlindTemp,
            };
            const exemptionData = { is_exempt: isExemptFromWithholding, exemption: exemptionTemp };
            const data = {
              ...stepOneData, ...exemptionData, ...claimData, ...ageAndBlindnessData, ...additionalData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderExemptionsQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default VirginiaForm;
